import { useMemo, useState } from 'react'
import type { OpportunityIncentive } from '@/lib'
import { convertPeriodOnToFiscalPeriod, getBasePeriods, getCurrentPeriod } from '@/lib/period-utils'

interface PeriodSelectionData {
  incentives: OpportunityIncentive[] | undefined
  accepts: { period: string }[] | undefined
}

export function usePeriodSelection({ incentives, accepts }: PeriodSelectionData) {
  // getCurrentPeriod()は現在日時から必ず有効な期を返す。万が一nullの場合は空文字をフォールバックとする
  const [selectedPeriod, setSelectedPeriod] = useState(getCurrentPeriod() ?? '')

  // 利用可能な期のリスト（基本期間 + インセンティブデータから一意の期を抽出してソート）
  const availablePeriods = useMemo(() => {
    const basePeriods = getBasePeriods()
    const allPeriods = new Set<string>(basePeriods)

    // インセンティブデータからの期間を追加（YYYY-MM → FY25上期 形式に変換）
    if (incentives) {
      for (const inc of incentives) {
        const periodStr = convertPeriodOnToFiscalPeriod(inc.period)
        if (periodStr) {
          allPeriods.add(periodStr)
        }
      }
    }

    return Array.from(allPeriods).sort((a, b) => a.localeCompare(b))
  }, [incentives])

  // 期でフィルタリングされたインセンティブデータ
  const filteredIncentives = useMemo(() => {
    if (!incentives) return []
    return incentives.filter((inc) => convertPeriodOnToFiscalPeriod(inc.period) === selectedPeriod)
  }, [incentives, selectedPeriod])

  // 選択された期が確認済みかどうか
  const isIncentiveAccepted = useMemo(() => {
    if (!accepts) return false
    return accepts.some((accept) => convertPeriodOnToFiscalPeriod(accept.period) === selectedPeriod)
  }, [accepts, selectedPeriod])

  return {
    selectedPeriod,
    setSelectedPeriod,
    availablePeriods,
    filteredIncentives,
    isIncentiveAccepted,
  }
}
